import { ButtonV2, Checkbox, Spinner, TooltipV2 } from '@cimplico/components';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useFileRecords } from '../../queries/use-file-records';
import { useViewLink } from '../../queries/use-view-link';
import { setViewingDocument } from '../../slices/documentViewSlice';
import { FileRecordNew } from '../../types/file-record';
import { calcRelativeTime, formatDateTime } from '../../utilities/formatting';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { useIsWorkpaperRole } from '../../hooks/use-is-workpaper-role';
import FileStatusIcon from '../files/FileStatusIcon';
import ViewErrorDialog from '../files/ViewErrorDialog';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import DocumentClearCheckOutConfirm from '../files/ConfirmationModals/DocumentClearCheckOutConfirm';
import SummaryTableTitle from './SummaryTableTitle';
import queryClient from '../../query-client';

function CheckedOutFiles(): JSX.Element {
    const { id, readOnly } = useWorkpaperContext();
    const { isGuest } = useIsWorkpaperRole();
    const [selectedFiles, setSelectedFiles] = useState<FileRecordNew[]>([]);
    const [checkingIn, setCheckingIn] = useState(false);

    const fileRecords = useFileRecords(1, 100, id, id, 'status=CHECKED_OUT', 'sort[dir]=asc&sort[field]=createdAt', '', true);

    function handleFileSelect(file: FileRecordNew): void {
        setSelectedFiles((prev) => {
            if (prev.find((p) => p.id === file.id)) {
                return prev.filter((p) => p.id !== file.id);
            }
            return prev.concat([file]);
        });
    }

    return (
        <>
            {checkingIn && (
                <DocumentClearCheckOutConfirm
                    fileRecords={selectedFiles}
                    onClose={(): void => {
                        setCheckingIn(false);
                        setSelectedFiles([]);
                        queryClient.invalidateQueries(['workpapers', id, 'certification']);
                    }}
                />
            )}
            <div>
                <div className="border-b pb-3 flex justify-between items-center">
                    <SummaryTableTitle
                        title="Files"
                        count={fileRecords.data ? (fileRecords.data.totalItems as number).toString() : '0'}
                        status="Checked Out"
                        statusBadgeColor="red"
                    />
                    {/* <Link to="../files?flat=true&status=CHECKED_OUT" className="text-xs font-medium text-gray-700">
                        See all
                    </Link> */}
                    {selectedFiles.length > 0 && (
                        <div className="-mt-2">
                            <ButtonV2
                                label={`Mark as Checked In (${selectedFiles.length})`}
                                size="xs"
                                onClick={(): void => setCheckingIn(true)}
                                isDisabled={readOnly || isGuest}
                                type="secondary"
                            />
                        </div>
                    )}
                </div>
                {fileRecords.isLoading && (
                    <div className="flex w-full justify-center pt-4">
                        <Spinner size="sm" />
                    </div>
                )}
                {fileRecords.isError && <p className="block text-center text-sm text-gray-500 pt-4">Unable to get files.</p>}
                {fileRecords.isSuccess && (
                    <div className="max-h-[350px] overflow-y-auto">
                        {fileRecords.data.totalItems === 0 ? (
                            <div className="mt-12">
                                <EmptyStateNew
                                    icon={<DocumentIcon />}
                                    title="No checked-out files"
                                    titleSize="small"
                                    helpText="See any checked out files preventing certification."
                                    helpLink="https://learn.cimplico.com/docs/files-overview#checked-out"
                                />
                            </div>
                        ) : (
                            <div className="mt-2">
                                {fileRecords.data.data.records.map((fr) => {
                                    return <File key={fr.id} record={fr} onSelect={handleFileSelect} />;
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

type FileProps = {
    record: FileRecordNew;
    onSelect: (file: FileRecordNew) => void;
};

function File({ record, onSelect }: FileProps): JSX.Element {
    const dispatch = useDispatch();
    const [viewErrorRecord, setViewErrorDocument] = useState<FileRecordNew | undefined>();

    async function handleDocumentView(): Promise<void> {
        if (record.id) {
            dispatch(setViewingDocument({ record }));
        }
    }

    return (
        <>
            {viewErrorRecord && <ViewErrorDialog onClose={(): void => setViewErrorDocument(undefined)} record={viewErrorRecord} />}
            <div className="flex items-center justify-between p-2 border-b">
                <div className="flex items-center gap-4 py-1">
                    <Checkbox onClick={(): void => onSelect(record)} />
                    <div className="flex items-center gap-2">
                        <FileStatusIcon status={record.status} />
                        <Link
                            to={`../files?folder=${record.path[record.path.length - 2].id}`}
                            onClick={handleDocumentView}
                            className="font-medium text-indigo-600 dark:text-indigo-400 text-sm cursor-pointer text-left"
                        >
                            {record.name}
                        </Link>
                    </div>
                </div>

                <div>
                    <TooltipV2 message={formatDateTime(record.updatedAt)} position="left">
                        <p className="text-gray-700 text-sm">{calcRelativeTime(record.updatedAt)}</p>
                    </TooltipV2>
                </div>
            </div>
        </>
    );
}

export default CheckedOutFiles;
